import React, { useState, useEffect } from 'react';
import './AuthModal.css';
import RecoveryModal from './RecoveryModal';
import { useAuth } from './contexts/AuthContext';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ open, onClose }) => {
  const { login, register, error, isLoading, clearError, generateWalletAddress, generateSeedPhrase } = useAuth();
  const [isRightPanelActive, setIsRightPanelActive] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    privateKey: '',
    seedPhrase: '',
    walletAddress: ''
  });
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showSeedPhrase, setShowSeedPhrase] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Limpiar errores cuando se abre el modal
  useEffect(() => {
    if (open) {
      clearError();
      setLocalError(null);
      setSuccessMessage(null);
      setHasAttemptedAuth(false);
    }
  }, [open, clearError]);

  // Cerrar modal si el usuario se autentica exitosamente
  const [hasAttemptedAuth, setHasAttemptedAuth] = useState(false);
  
  useEffect(() => {
    // Solo cerrar si no está cargando, no hay errores, y se ha intentado autenticar
    if (!isLoading && !error && hasAttemptedAuth) {
      onClose();
      setHasAttemptedAuth(false);
    }
  }, [isLoading, error, onClose, hasAttemptedAuth]);

  if (!open) return null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleGenerateWalletAddress = () => {
    const mockAddress = generateWalletAddress();
    setFormData({
      ...formData,
      walletAddress: mockAddress
    });
  };

  const handleGenerateSeedPhrase = () => {
    const mockSeedPhrase = generateSeedPhrase();
    setFormData({
      ...formData,
      seedPhrase: mockSeedPhrase
    });
  };

  // Spinner SVG
  const Spinner = () => (
    <svg style={{ marginRight: 8, verticalAlign: 'middle' }} width="18" height="18" viewBox="0 0 50 50">
      <circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" strokeDasharray="31.415, 31.415" transform="rotate(72.0001 25 25)">
        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite" />
      </circle>
    </svg>
  );

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones mejoradas
    if (!validateEmail(formData.email)) {
      setLocalError('El email no es válido');
      return;
    }
    if (formData.password.length < 6) {
      setLocalError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setLocalError('Las contraseñas no coinciden');
      return;
    }
    if (!formData.seedPhrase) {
      setLocalError('Debes generar una frase semilla');
      return;
    }
    if (!formData.walletAddress) {
      setLocalError('Debes generar una dirección de wallet');
      return;
    }

    setHasAttemptedAuth(true);
    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        seedPhrase: formData.seedPhrase,
        walletAddress: formData.walletAddress
      });
      setSuccessMessage('¡Registro exitoso!');
    } catch (error) {
      setLocalError('Error en el registro');
      setHasAttemptedAuth(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones mejoradas
    if (!validateEmail(formData.email)) {
      setLocalError('El email no es válido');
      return;
    }
    if (formData.password.length < 6) {
      setLocalError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setHasAttemptedAuth(true);
    try {
      await login({
        email: formData.email,
        password: formData.password,
        privateKey: formData.privateKey || undefined
      });
      setSuccessMessage('¡Login exitoso!');
    } catch (error) {
      setLocalError('Error en el login');
      setHasAttemptedAuth(false);
    }
  };

  const handleRecoveryClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowRecovery(true);
  };

  const handleRecoveryClose = () => {
    setShowRecovery(false);
  };

  return (
    <>
      <div className="auth-modal-overlay" onClick={onClose}>
        <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className={`container ${isRightPanelActive ? 'right-panel-active' : ''}`} id="container">
            <div className="form-container sign-up-container">
              <form onSubmit={handleSignUp}>
                <h1>Crear Wallet</h1>
                <div className="social-container">
                  <a href="#" className="social"><i className="fab fa-google"></i></a>
                  <a href="#" className="social"><i className="fab fa-facebook-f"></i></a>
                  <a href="#" className="social"><i className="fab fa-apple"></i></a>
                </div>
                <span>Configura tu wallet segura</span>
                
                {/* Mostrar errores */}
                {(error || localError) && (
                  <div style={{ 
                    color: '#ff4444', 
                    fontSize: '12px', 
                    marginBottom: '10px',
                    textAlign: 'center',
                    padding: '8px',
                    backgroundColor: 'rgba(255, 68, 68, 0.1)',
                    borderRadius: '4px',
                    border: '1px solid #ff4444'
                  }}>
                    <i className="fas fa-exclamation-triangle" style={{ marginRight: '5px' }}></i>
                    {error || localError}
                  </div>
                )}
                {/* Mostrar mensajes de éxito */}
                {successMessage && (
                  <div style={{ 
                    color: '#4CAF50', 
                    fontSize: '12px', 
                    marginBottom: '10px',
                    textAlign: 'center',
                    padding: '8px',
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '4px',
                    border: '1px solid #4CAF50'
                  }}>
                    <i className="fas fa-check-circle" style={{ marginRight: '5px' }}></i>
                    {successMessage}
                  </div>
                )}
                
                <input 
                  type="text" 
                  placeholder="Nombre completo" 
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                <input 
                  type="email" 
                  placeholder="Email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                <input 
                  type="password" 
                  placeholder="Contraseña" 
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                />
                <input 
                  type="password" 
                  placeholder="Confirmar contraseña" 
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                />
                
                <div className="wallet-section">
                  <h3>Configuración de Wallet</h3>
                  <button 
                    type="button" 
                    className="generate-btn"
                    onClick={handleGenerateSeedPhrase}
                  >
                    Generar Frase Semilla
                  </button>
                  {formData.seedPhrase && (
                    <div className="seed-phrase-container">
                      <label>Frase Semilla:</label>
                      <div className="seed-phrase-display">
                        <input 
                          type={showSeedPhrase ? "text" : "password"}
                          value={formData.seedPhrase}
                          readOnly
                          className="seed-phrase-input"
                        />
                        <button 
                          type="button" 
                          className="toggle-btn"
                          onClick={() => setShowSeedPhrase(!showSeedPhrase)}
                        >
                          <i className={`fas fa-${showSeedPhrase ? 'eye-slash' : 'eye'}`}></i>
                        </button>
                      </div>
                      <small>⚠️ Guarda esta frase en un lugar seguro</small>
                    </div>
                  )}
                  
                  <button 
                    type="button" 
                    className="generate-btn"
                    onClick={handleGenerateWalletAddress}
                  >
                    Generar Dirección
                  </button>
                  {formData.walletAddress && (
                    <div className="wallet-address-container">
                      <label>Dirección de Wallet:</label>
                      <input 
                        type="text"
                        value={formData.walletAddress}
                        readOnly
                        className="wallet-address-input"
                      />
                    </div>
                  )}
                </div>
                
                <button type="submit" className="create-wallet-btn" disabled={isLoading}>
                  {isLoading ? <Spinner /> : null}
                  {isLoading ? 'Creando Wallet...' : 'Crear Wallet'}
                </button>
              </form>
            </div>
            <div className="form-container sign-in-container">
              <form onSubmit={handleSignIn}>
                <h1>Acceder a Wallet</h1>
                <div className="social-container">
                  <a href="#" className="social"><i className="fab fa-google"></i></a>
                  <a href="#" className="social"><i className="fab fa-facebook-f"></i></a>
                  <a href="#" className="social"><i className="fab fa-apple"></i></a>
                </div>
                <span>Accede con tu wallet</span>
                
                {/* Mostrar errores */}
                {(error || localError) && (
                  <div style={{ 
                    color: '#ff4444', 
                    fontSize: '12px', 
                    marginBottom: '10px',
                    textAlign: 'center',
                    padding: '8px',
                    backgroundColor: 'rgba(255, 68, 68, 0.1)',
                    borderRadius: '4px',
                    border: '1px solid #ff4444'
                  }}>
                    <i className="fas fa-exclamation-triangle" style={{ marginRight: '5px' }}></i>
                    {error || localError}
                  </div>
                )}
                {/* Mostrar mensajes de éxito */}
                {successMessage && (
                  <div style={{ 
                    color: '#4CAF50', 
                    fontSize: '12px', 
                    marginBottom: '10px',
                    textAlign: 'center',
                    padding: '8px',
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '4px',
                    border: '1px solid #4CAF50'
                  }}>
                    <i className="fas fa-check-circle" style={{ marginRight: '5px' }}></i>
                    {successMessage}
                  </div>
                )}
                
                <input 
                  type="email" 
                  placeholder="Email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                <input 
                  type="password" 
                  placeholder="Contraseña" 
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                
                <div className="private-key-section">
                  <label>Llave Privada (opcional):</label>
                  <div className="private-key-container">
                    <input 
                      type={showPrivateKey ? "text" : "password"}
                      placeholder="Llave privada (hex)"
                      name="privateKey"
                      value={formData.privateKey}
                      onChange={handleInputChange}
                      className="private-key-input"
                    />
                    <button 
                      type="button" 
                      className="toggle-btn"
                      onClick={() => setShowPrivateKey(!showPrivateKey)}
                    >
                      <i className={`fas fa-${showPrivateKey ? 'eye-slash' : 'eye'}`}></i>
                    </button>
                  </div>
                  <small>⚠️ Solo ingresa tu llave privada si confías en este dispositivo</small>
                </div>
                
                <a href="#" onClick={handleRecoveryClick}>¿Olvidaste tu contraseña?</a>
                <button type="submit" disabled={isLoading}>
                  {isLoading ? <Spinner /> : null}
                  {isLoading ? 'Accediendo...' : 'Acceder a Wallet'}
                </button>
              </form>
            </div>
            <div className="overlay-container">
              <div className="overlay">
                <div className="overlay-panel overlay-left">
                  <h1>¡Bienvenido de vuelta!</h1>
                  <p>Accede a tu wallet segura y gestiona tus criptomonedas</p>
                  <button 
                    className="ghost" 
                    id="signIn"
                    onClick={() => setIsRightPanelActive(false)}
                  >
                    Acceder
                  </button>
                </div>
                <div className="overlay-panel overlay-right">
                  <h1>¡Crea tu Wallet!</h1>
                  <p>Configura tu wallet segura y comienza a gestionar tus criptomonedas</p>
                  <button 
                    className="ghost" 
                    id="signUp"
                    onClick={() => setIsRightPanelActive(true)}
                  >
                    Crear Wallet
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <RecoveryModal open={showRecovery} onClose={handleRecoveryClose} />
    </>
  );
};

export default AuthModal; 